import { useAtom } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { isLoggedInAtom } from '@root/entities/auth'
import {
  tradeSwapTypeAtom,
  tradingPairAtom,
} from '@root/features/trade/trading'

import { TradingHeadingTabs } from '../common/trading-heading-tabs'
import { TradeSettingsType } from '../common/trading-settings-type'

import { TradingControlsBuyForm } from './trading-controls-buy/trading-controls-buy-form'
import { TradingControlsSellForm } from './trading-controls-sell/trading-controls-sell-form'
import { TradingControlsSkeleton } from './trading-controls-skeleton'
import { TradingGas } from './trading-gas'

import type { BuyControlsModel, SellControlsModel } from '../../atoms/common'

type Props = {
  onSettingsOpen: () => void
  buyControlsModel: BuyControlsModel
  sellControlsModel: SellControlsModel
}

export const TradingControls: React.FC<Props> = ({
  onSettingsOpen,
  buyControlsModel,
  sellControlsModel,
}) => {
  const { t } = useTranslation()

  const [activePair] = useAtom(tradingPairAtom)
  const [tradeSwapType, setTradeSwapType] = useAtom(tradeSwapTypeAtom)
  const [isLoggedIn] = useAtom(isLoggedInAtom)

  const renderContent = () => {
    if (!activePair) {
      return <TradingControlsSkeleton faded={false} />
    }

    return (
      <Container $faded={false}>
        <TradeSettingsType
          swapDirection={tradeSwapType}
          onChange={(type) => setTradeSwapType(type)}
          buyLabel={t('trading.buy')}
          sellLabel={t('trading.sell')}
        />
        <TradingHeadingTabs>
          {isLoggedIn ? (
            <TradingGas onSettingsOpen={onSettingsOpen} />
          ) : undefined}
        </TradingHeadingTabs>
        {tradeSwapType === 'buy' ? (
          <TradingControlsBuyForm pair={activePair} model={buyControlsModel} />
        ) : (
          <TradingControlsSellForm
            pair={activePair}
            model={sellControlsModel}
          />
        )}
      </Container>
    )
  }

  return <Root>{renderContent()}</Root>
}

const Root = styled.div`
  position: relative;

  overflow: hidden;
`

const Container = styled.div<{ $faded: boolean }>`
  display: grid;
  grid-template-rows: auto 1fr;
  background: var(--background-1);
  padding-block: 16px;

  ${({ $faded }) =>
    $faded &&
    css`
      opacity: 0.25;
      filter: grayscale(1);
    `}
`
